import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/AuthContext';
import { db } from '../../lib/firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import { 
  Award, 
  BookOpen, 
  Target, 
  Trophy, 
  Star,
  CheckCircle,
  Lock,
  Flame,
  Zap,
  Clock
} from 'lucide-react';

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: any;
  condition: (data: UserAchievements) => boolean;
  progress?: (data: UserAchievements) => { current: number; total: number };
}

interface UserAchievements {
  unlocked: string[];
  currentStreak: number;
  longestStreak: number;
  moduleCompletions: number;
  journalEntries: number;
  perfectQuizScores: number;
  dailyChallengesCompleted: number;
  totalTimeSpent: number;
}

const ACHIEVEMENTS: Achievement[] = [
  {
    id: 'first_steps',
    name: 'First Steps',
    description: 'Complete your first training module',
    icon: BookOpen,
    condition: (data) => data.moduleCompletions >= 1,
    progress: (data) => ({ current: Math.min(data.moduleCompletions, 1), total: 1 })
  },
  {
    id: 'quick_learner',
    name: 'Quick Learner',
    description: 'Complete 5 training modules',
    icon: Zap,
    condition: (data) => data.moduleCompletions >= 5,
    progress: (data) => ({ current: Math.min(data.moduleCompletions, 5), total: 5 })
  },
  {
    id: 'dedicated_student',
    name: 'Dedicated Student',
    description: 'Complete 10 training modules',
    icon: Target,
    condition: (data) => data.moduleCompletions >= 10,
    progress: (data) => ({ current: Math.min(data.moduleCompletions, 10), total: 10 })
  },
  {
    id: 'expert_learner',
    name: 'Expert Learner',
    description: 'Complete 20 training modules',
    icon: Trophy,
    condition: (data) => data.moduleCompletions >= 20,
    progress: (data) => ({ current: Math.min(data.moduleCompletions, 20), total: 20 })
  },
  {
    id: 'week_warrior',
    name: 'Week Warrior',
    description: 'Maintain a 7-day check-in streak',
    icon: Flame,
    condition: (data) => data.currentStreak >= 7,
    progress: (data) => ({ current: Math.min(data.currentStreak, 7), total: 7 })
  },
  {
    id: 'perfect_score',
    name: 'Perfect Score',
    description: 'Achieve a perfect score on a quiz',
    icon: Star,
    condition: (data) => data.perfectQuizScores >= 1,
    progress: (data) => ({ current: Math.min(data.perfectQuizScores, 1), total: 1 })
  },
  {
    id: 'journal_keeper',
    name: 'Journal Keeper',
    description: 'Complete 10 journal entries',
    icon: BookOpen,
    condition: (data) => data.journalEntries >= 10,
    progress: (data) => ({ current: Math.min(data.journalEntries, 10), total: 10 })
  },
  {
    id: 'time_invested',
    name: 'Time Invested',
    description: 'Spend 5 hours learning',
    icon: Clock,
    condition: (data) => data.totalTimeSpent >= 300, // 300 minutes = 5 hours
    progress: (data) => ({ current: Math.min(data.totalTimeSpent, 300), total: 300 })
  }
];

// Challenge Coin Requirements
const CHALLENGE_COIN_REQUIREMENTS = [
  { id: 'modules', label: 'Complete 20 Training Modules', threshold: 20, field: 'moduleCompletions' },
  { id: 'quizzes', label: 'Score 100% on 5 Quizzes', threshold: 5, field: 'perfectQuizScores' },
  { id: 'streak', label: 'Maintain a 30-Day Streak', threshold: 30, field: 'longestStreak' },
  { id: 'time', label: 'Complete 20 Hours of Training', threshold: 1200, field: 'totalTimeSpent' } // 1200 minutes = 20 hours
];

export const AchievementSystem: React.FC = () => {
  const { user } = useAuth();
  const [userAchievements, setUserAchievements] = useState<UserAchievements>({
    unlocked: [],
    currentStreak: 0,
    longestStreak: 0,
    moduleCompletions: 0,
    journalEntries: 0,
    perfectQuizScores: 0,
    dailyChallengesCompleted: 0,
    totalTimeSpent: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    fetchUserAchievements();
  }, [user]);

  const fetchUserAchievements = async () => {
    if (!user) return;

    try {
      // Fetch user achievements document
      const achievementsDoc = await getDoc(doc(db, 'users', user.uid, 'achievements', 'summary'));
      
      // Fetch completed modules count
      const completedModulesQuery = query(
        collection(db, 'users', user.uid, 'completedModules')
      );
      const completedModulesSnap = await getDocs(completedModulesQuery);
      const moduleCompletions = completedModulesSnap.size;

      // Fetch journal entries count
      const journalsQuery = query(
        collection(db, 'users', user.uid, 'journals')
      );
      const journalsSnap = await getDocs(journalsQuery);
      
      const promptedJournalsQuery = query(
        collection(db, 'users', user.uid, 'promptedJournals')
      );
      const promptedJournalsSnap = await getDocs(promptedJournalsQuery);
      const journalEntries = journalsSnap.size + promptedJournalsSnap.size;

      // Fetch perfect quiz scores
      const quizResultsQuery = query(
        collection(db, 'users', user.uid, 'quizResults'),
        where('percentage', '==', 100)
      );
      const quizResultsSnap = await getDocs(quizResultsQuery);
      const perfectQuizScores = quizResultsSnap.size;

      // Fetch streak data from user document
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.data();
      const currentStreak = userData?.currentStreak || 0;
      const longestStreak = userData?.longestStreak || 0;

      // Calculate total time spent (sum of all module completion times)
      let totalTimeSpent = 0;
      completedModulesSnap.forEach((doc) => {
        const data = doc.data();
        if (data.timeSpent) {
          totalTimeSpent += data.timeSpent;
        }
      });

      const unlockedAchievements = achievementsDoc.exists() 
        ? achievementsDoc.data().unlocked || []
        : [];

      setUserAchievements({
        unlocked: unlockedAchievements,
        currentStreak,
        longestStreak,
        moduleCompletions,
        journalEntries,
        perfectQuizScores,
        dailyChallengesCompleted: 0,
        totalTimeSpent: Math.round(totalTimeSpent / 60) // Convert seconds to minutes
      });
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAchievementProgress = (achievement: Achievement): number => {
    if (!achievement.progress) return 0;
    const { current, total } = achievement.progress(userAchievements);
    return (current / total) * 100;
  };

  const isAchievementUnlocked = (achievement: Achievement): boolean => {
    return userAchievements.unlocked.includes(achievement.id) || achievement.condition(userAchievements);
  };

  const getChallengeCoinProgress = (): number => {
    const completed = CHALLENGE_COIN_REQUIREMENTS.filter(req => {
      const value = userAchievements[req.field as keyof UserAchievements] as number;
      return value >= req.threshold;
    }).length;
    return (completed / CHALLENGE_COIN_REQUIREMENTS.length) * 100;
  };

  const isChallengeCoinEarned = (): boolean => {
    return getChallengeCoinProgress() === 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Challenge Coin Section */}
      <div className="bg-gradient-to-br from-yellow-500 via-yellow-600 to-orange-600 rounded-2xl p-8 text-white shadow-2xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-3xl font-bold mb-2">GGG Challenge Coin</h2>
            <p className="text-yellow-100">Complete all requirements to earn your challenge coin</p>
          </div>
          <div className="relative">
            {isChallengeCoinEarned() ? (
              <div className="w-32 h-32 relative">
                <img 
                  src="/GGGChallengeCoin.png" 
                  alt="GGG Challenge Coin" 
                  className="w-full h-full object-contain drop-shadow-2xl animate-pulse"
                />
              </div>
            ) : (
              <div className="w-32 h-32 relative">
                <img 
                  src="/GGGChallengeCoin.png" 
                  alt="GGG Challenge Coin" 
                  className="w-full h-full object-contain opacity-30 grayscale blur-sm"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock className="w-12 h-12 text-white drop-shadow-lg" />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-yellow-100">Overall Progress</span>
            <span className="text-sm font-bold">{Math.round(getChallengeCoinProgress())}%</span>
          </div>
          <div className="w-full bg-white bg-opacity-20 rounded-full h-3">
            <div 
              className="bg-white h-3 rounded-full transition-all duration-500"
              style={{ width: `${getChallengeCoinProgress()}%` }}
            ></div>
          </div>
        </div>

        {/* Requirements */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {CHALLENGE_COIN_REQUIREMENTS.map((req) => {
            const value = userAchievements[req.field as keyof UserAchievements] as number;
            const completed = value >= req.threshold;
            const progress = Math.min((value / req.threshold) * 100, 100);

            return (
              <div key={req.id} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-4 border border-white border-opacity-20">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{req.label}</span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-bold">{value}/{req.threshold}</span>
                    <div className={`w-3 h-3 rounded-full ${
                      completed ? 'bg-green-400' : 
                      progress >= 50 ? 'bg-yellow-300' : 
                      'bg-white bg-opacity-30'
                    }`}></div>
                  </div>
                </div>
                <div className="w-full bg-white bg-opacity-20 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full transition-all duration-500 ${
                      completed ? 'bg-green-400' : 'bg-white'
                    }`}
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {isChallengeCoinEarned() && (
          <div className="mt-6 bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-30">
            <div className="flex items-center space-x-4">
              <img 
                src="/GGGChallengeCoin.png" 
                alt="GGG Challenge Coin" 
                className="w-16 h-16 object-contain drop-shadow-xl animate-bounce"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <CheckCircle className="w-6 h-6 text-green-300" />
                  <p className="font-bold text-lg">Congratulations! You've earned your GGG Challenge Coin!</p>
                </div>
                <p className="text-sm text-yellow-100">Your dedication to growth and resilience is inspiring. You are a true warrior!</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Regular Achievements */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Achievements</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ACHIEVEMENTS.map((achievement) => {
            const unlocked = isAchievementUnlocked(achievement);
            const progress = getAchievementProgress(achievement);
            const Icon = achievement.icon;

            return (
              <div
                key={achievement.id}
                className={`rounded-xl p-6 transition-all duration-300 ${
                  unlocked
                    ? 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-1'
                    : 'bg-gray-100 text-gray-600 border-2 border-gray-200'
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
                    unlocked ? 'bg-white bg-opacity-20' : 'bg-gray-200'
                  }`}>
                    <Icon className="w-7 h-7" />
                  </div>
                  {unlocked && (
                    <CheckCircle className="w-6 h-6 text-white" />
                  )}
                  {!unlocked && (
                    <Lock className="w-5 h-5 text-gray-400" />
                  )}
                </div>

                <h3 className="text-lg font-bold mb-2">{achievement.name}</h3>
                <p className={`text-sm mb-4 ${unlocked ? 'text-blue-100' : 'text-gray-500'}`}>
                  {achievement.description}
                </p>

                {!unlocked && achievement.progress && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-600">Progress</span>
                      <span className="text-xs font-bold text-gray-700">{Math.round(progress)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                )}

                {unlocked && (
                  <div className="text-sm font-medium text-blue-100">
                    Unlocked! 🎉
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Your Stats</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-3xl font-bold text-blue-600">{userAchievements.moduleCompletions}</div>
            <div className="text-sm text-gray-600">Modules Completed</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-purple-600">{userAchievements.journalEntries}</div>
            <div className="text-sm text-gray-600">Journal Entries</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-orange-600">{userAchievements.currentStreak}</div>
            <div className="text-sm text-gray-600">Current Streak</div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-bold text-green-600">{userAchievements.perfectQuizScores}</div>
            <div className="text-sm text-gray-600">Perfect Quizzes</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Export the checkAchievements function for use in other components
export async function checkAchievements(
  user: any,
  moduleCompleted?: boolean,
  quizScore?: number,
  timeSpent?: number
): Promise<void> {
  if (!user) return;

  try {
    const achievementsRef = doc(db, 'users', user.uid, 'achievements', 'summary');
    const achievementsDoc = await getDoc(achievementsRef);
    
    // Fetch current data
    const completedModulesQuery = query(
      collection(db, 'users', user.uid, 'completedModules')
    );
    const completedModulesSnap = await getDocs(completedModulesQuery);
    const currentModuleCount = completedModulesSnap.size;

    const quizResultsQuery = query(
      collection(db, 'users', user.uid, 'quizResults'),
      where('percentage', '==', 100)
    );
    const quizResultsSnap = await getDocs(quizResultsQuery);
    const currentPerfectQuizCount = quizResultsSnap.size;

    let currentTimeSpent = 0;
    completedModulesSnap.forEach((doc) => {
      const data = doc.data();
      if (data.timeSpent) {
        currentTimeSpent += data.timeSpent;
      }
    });
    currentTimeSpent = Math.round(currentTimeSpent / 60); // Convert to minutes

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.data();
    
    const currentUnlocked = achievementsDoc.exists() 
      ? achievementsDoc.data().unlocked || []
      : [];

    const newUnlocked: string[] = [];

    // Check each achievement
    const userAchievements = {
      unlocked: currentUnlocked,
      currentStreak: userData?.currentStreak || 0,
      longestStreak: userData?.longestStreak || 0,
      moduleCompletions: currentModuleCount,
      journalEntries: 0, // This would need to be calculated if checking journal achievements
      perfectQuizScores: currentPerfectQuizCount,
      dailyChallengesCompleted: 0,
      totalTimeSpent: currentTimeSpent
    };

    ACHIEVEMENTS.forEach(achievement => {
      if (!currentUnlocked.includes(achievement.id) && achievement.condition(userAchievements)) {
        newUnlocked.push(achievement.id);
      }
    });

    // Update achievements if new ones were unlocked
    if (newUnlocked.length > 0) {
      await setDoc(achievementsRef, {
        unlocked: [...currentUnlocked, ...newUnlocked],
        lastUpdated: serverTimestamp()
      }, { merge: true });
    }
  } catch (error) {
    console.error('Error checking achievements:', error);
  }
}

export default AchievementSystem;
